import { forwardRef } from 'react';
import PropTypes from 'prop-types';
import { CARD_STYLES } from '../constants';
import { useLanguage } from '@shared/contexts/LanguageContext';

const formatWishDate = (value) => {
    if (!value) return null;
    const dateObj = new Date(value);
    if (Number.isNaN(dateObj.getTime())) return null;
    const m = String(dateObj.getMonth() + 1).padStart(2, '0');
    const d = String(dateObj.getDate()).padStart(2, '0');
    return `${dateObj.getFullYear()}/${m}/${d}`;
};

/**
 * 單張發願卡（card1~4 背景樣式 + 文字疊印）
 * size: 'slot'（掛在樹上的縮小版）| 'full'（表單/結果 popup 用的完整版）
 * 中文：直式（vertical-rl）；其他語言：橫式，版面與中文分開處理避免互相打架
 */
const WishCard = forwardRef(({ wish, size = 'full', className = '', style, textScale = 1 }, ref) => {
    const cardImage = CARD_STYLES.find((c) => c.id === wish.image_id)?.image || CARD_STYLES[0].image;
    const isSlot = size === 'slot';
    const { currentLanguage } = useLanguage();
    const isZh = currentLanguage.code === 'zh-TW';
    const dateStr = formatWishDate(wish.created_at);

    return (
        <div
            ref={ref}
            className={`relative ${className}`}
            style={{
                aspectRatio: '659 / 1241',
                width: isSlot ? '100%' : 'min(42vw, 180px)',
                ...style,
            }}
        >
            <img src={cardImage} alt="" className="absolute inset-0 w-full h-full object-contain" draggable={false} />
            {!isSlot && (
                <>
                    {dateStr && (
                        <div
                            className="absolute font-medium"
                            style={{
                                left: '9%',
                                top: '5%',
                                color: '#5A3B22',
                                opacity: 0.75,
                                whiteSpace: 'nowrap',
                                writingMode: 'horizontal-tb',
                                fontSize: `calc(clamp(8px, 2vw, 10px) * 1.3 * ${textScale})`,
                                lineHeight: 1.2,
                            }}
                        >
                            {dateStr}
                        </div>
                    )}
                    {isZh ? (
                        <>
                            <div
                                className="absolute flex flex-col items-center text-center justify-center"
                                style={{
                                    left: '9%',
                                    right: '9%',
                                    top: '20%',
                                    bottom: '20%',
                                    padding: '4% 2%',
                                    color: '#5A3B22',
                                    overflow: 'hidden',
                                }}
                            >
                                <p
                                    className="font-medium"
                                    style={{
                                        writingMode: 'vertical-rl',
                                        textOrientation: 'upright',
                                        whiteSpace: 'nowrap',
                                        fontSize: `calc(clamp(10px, 2.5vw, 12px) * 2 * ${textScale})`,
                                        lineHeight: 1.4,
                                    }}
                                >
                                    {wish.content}
                                </p>
                            </div>
                            <div
                                className="absolute font-semibold text-center"
                                style={{
                                    left: '9%',
                                    right: '9%',
                                    bottom: '9%',
                                    color: '#5A3B22',
                                    writingMode: 'horizontal-tb',
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    fontSize: `calc(clamp(9px, 2.3vw, 11px) * 1.5 * ${textScale})`,
                                    lineHeight: 1.4,
                                }}
                            >
                                {wish.vower_name}
                            </div>
                        </>
                    ) : (
                        <>
                            <div
                                className="absolute flex flex-col items-center text-center justify-center"
                                style={{
                                    left: '9%',
                                    right: '9%',
                                    top: '28%',
                                    bottom: '20%',
                                    padding: '2% 4%',
                                    color: '#5A3B22',
                                    overflow: 'hidden',
                                }}
                            >
                                <p
                                    className="font-medium"
                                    style={{
                                        writingMode: 'horizontal-tb',
                                        fontSize: `calc(clamp(8px, 2.1vw, 10px) * 1.5 * ${textScale})`,
                                        lineHeight: 1.4,
                                    }}
                                >
                                    {wish.content}
                                </p>
                            </div>
                            <div
                                className="absolute font-semibold text-center"
                                style={{
                                    left: '9%',
                                    right: '9%',
                                    bottom: '9%',
                                    color: '#5A3B22',
                                    writingMode: 'horizontal-tb',
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    fontSize: `calc(clamp(8px, 2vw, 10px) * 1.3 * ${textScale})`,
                                    lineHeight: 1.4,
                                }}
                            >
                                {wish.vower_name}
                            </div>
                        </>
                    )}
                </>
            )}
        </div>
    );
});

WishCard.displayName = 'WishCard';

WishCard.propTypes = {
    wish: PropTypes.shape({
        vower_name: PropTypes.string,
        monastery_name: PropTypes.string,
        image_id: PropTypes.number,
        content: PropTypes.string,
        created_at: PropTypes.string,
    }).isRequired,
    size: PropTypes.oneOf(['slot', 'full']),
    className: PropTypes.string,
    style: PropTypes.object,
    textScale: PropTypes.number,
};

export default WishCard;
